import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { useSessionContext } from '../useSessionContext';
import {
  SessionEnding,
  SessionEndReason,
  SessionTimeRemaining,
  SessionUpdated
} from '../types/InteractiveSession';
import { pushAlert } from '../../login';
import { useSessionExpiry } from './useSessionExpiry';

export interface UseInteractiveSession {
  isExpiring: boolean;
  extending: boolean;
  secondsRemaining: number;
  logout: () => void;
  extend: () => void;
}

export function calcRemainingTimeInSeconds(
  remainingTime: SessionTimeRemaining | null
): number {
  return remainingTime
    ? remainingTime.days * 24 * 60 * 60 +
        remainingTime.hours * 60 * 60 +
        remainingTime.minutes * 60 +
        remainingTime.seconds +
        Math.round(remainingTime.milliseconds / 1000)
    : -1;
}

export const useInteractiveSession = (): UseInteractiveSession => {
  const { formatMessage } = useIntl();
  const isSessionV2Enabled = useFeatureFlag('isSessionV2Enabled');
  const {
    logout,
    expire,
    interactiveSession,
    isInitialized,
    hasValidToken,
    tokenRef
  } = useSessionContext();

  useMemo(() => {
    (interactiveSession.current as any).useSessionV2 = isSessionV2Enabled;
  }, [interactiveSession, isSessionV2Enabled]);

  const [timeRemainingInSeconds, setTimeRemainingInSeconds] = useState<number>(
    -1
  );

  const [extending, setExtending] = useState(false);

  const sessionReasonText = useMemo<{
    [key: string]: string;
  }>(
    () => ({
      [SessionEndReason.expired]: formatMessage({
        id: 'session.reason.expired'
      }),
      [SessionEndReason.different_user]: formatMessage({
        id: 'session.reason.different_user'
      }),
      [SessionEndReason.logout_elsewhere]: formatMessage({
        id: 'session.reason.logout_elsewhere'
      })
    }),
    [formatMessage]
  );

  useEffect(() => {
    const session = interactiveSession.current;

    const updateTime = (e: SessionUpdated): void => {
      const { remainingTime } = e;

      const remainingInSeconds = calcRemainingTimeInSeconds(remainingTime);
      setTimeRemainingInSeconds(remainingInSeconds);
    };

    const sessionEnding = (e: SessionEnding) => {
      if (sessionReasonText[e.reason]) {
        pushAlert({
          severity: 'info',
          message: sessionReasonText[e.reason],
          dismissible: false
        });
      }

      setTimeRemainingInSeconds(-1);
    };

    if (
      !interactiveSession.current?.isInitialized &&
      isInitialized &&
      hasValidToken &&
      tokenRef.current
    ) {
      interactiveSession.current
        ?.begin(tokenRef.current.accessToken as string)
        .then(() => {});
    }

    session?.addEventListener('updated', updateTime as EventListener);
    session?.addEventListener('ending', sessionEnding as EventListener);

    return () => {
      session?.removeEventListener('updated', updateTime as EventListener);
      session?.removeEventListener('ending', sessionEnding as EventListener);
    };
  }, [
    sessionReasonText,
    interactiveSession,
    isInitialized,
    hasValidToken,
    tokenRef,
    logout
  ]);

  const extend = useCallback(async () => {
    setExtending(true);
    await interactiveSession.current?.extend();
    setExtending(false);
  }, [interactiveSession]);

  const remaining = useCallback(() => {
    interactiveSession.current?.remaining();
  }, [interactiveSession]);

  const { isExpiring, secondsRemaining } = useSessionExpiry(
    timeRemainingInSeconds,
    extend,
    expire,
    remaining
  );

  return {
    isExpiring,
    extending,
    secondsRemaining,
    logout,
    extend
  };
};
